import { Fields } from "../types/fields";
import {
  FormConfig,
  FormConfigApplicationStructureItem,
  FormConfigItem,
  FormItemType,
} from "../types/form";

function getApplicationStructure(config: FormConfig): FormConfigApplicationStructureItem[] {
  if (!config.application) {
    // Config payloads occasionally arrive without `application` (FORMS-WEB-TC);
    // degrade gracefully instead of crashing the form.
    console.warn("Form config has no application; falling back to empty structure.");
    return [];
  }
  return config.application.structure ?? [];
}

export function getLookupFieldSlugs(config: FormConfig): Set<string> {
  const slugs = new Set<string>();
  const structure = getApplicationStructure(config);
  for (const item of config.form_state?.items ?? []) {
    const candidates = item.type === FormItemType.section ? item.params.items ?? [] : [item];
    for (const candidate of candidates) {
      if (candidate.type !== FormItemType.field) continue;
      const field = structure.find((f) => f.slug === candidate.slug);
      if (field?.field_type === Fields.lookupfield) slugs.add(candidate.slug);
    }
  }
  return slugs;
}

export function getFieldFromSlug(
  slug: string,
  config: FormConfig
): FormConfigApplicationStructureItem {
  const field = config.application.structure.find((item) => item.slug === slug);
  if (!field) {
    throw new Error(`No field found for form item of slug "${slug}"`);
  }
  return field;
}

export function isLookupFieldStale(
  formItem: FormConfigItem,
  appField: FormConfigApplicationStructureItem
): boolean {
  if (appField.field_type !== Fields.lookupfield) return false;

  if (!appField.params.target_field_structure) return true;

  const formFieldSelection = formItem.params.lookup_field_selection;
  const formLinkedField = formItem.params.lookup_linked_field;

  if (formFieldSelection == null && formLinkedField == null) return false;

  return (
    formFieldSelection !== appField.params.field_selection ||
    formLinkedField !== appField.params.linked_field
  );
}

export function filterItemsByFeatureFlags(
  items: FormConfigItem[],
  config: FormConfig
): FormConfigItem[] {
  const isTeamEnabled = config.feature_flags?.form_team_field === true;
  const isDateRangeEnabled = config.feature_flags?.form_date_range === true;
  const isChecklistEnabled = config.feature_flags?.form_checklist_field === true;

  if (isTeamEnabled && isDateRangeEnabled && isChecklistEnabled) return items;

  const structure = getApplicationStructure(config);
  const structureMap = new Map(structure.map((f) => [f.slug, f]));

  const isDisabledItem = (item: FormConfigItem): boolean => {
    if (item.type !== FormItemType.field) return false;
    const fieldType = structureMap.get(item.slug)?.field_type;
    if (!isTeamEnabled && fieldType === Fields.teamfield) return true;
    if (!isDateRangeEnabled && fieldType === Fields.daterangefield) return true;
    // The builder refuses to add a checklist without the flag, but a form built while it was on
    // must stop rendering the field when it is turned off — same kill switch as the two above.
    if (!isChecklistEnabled && fieldType === Fields.checklistfield) return true;
    return false;
  };

  return items.reduce<FormConfigItem[]>((acc, item) => {
    if (item.type === FormItemType.section && item.params.items) {
      const filteredSectionItems = item.params.items.filter((child) => !isDisabledItem(child));
      acc.push({ ...item, params: { ...item.params, items: filteredSectionItems } });
    } else if (!isDisabledItem(item)) {
      acc.push(item);
    }
    return acc;
  }, []);
}

export function getFormattedRedirectUrl(redirectUrl: string): string {
  if (redirectUrl && !redirectUrl.toLowerCase().includes("http")) {
    return "https://" + redirectUrl;
  }
  return redirectUrl;
}
